import React from "react";
import {
  CommentContent,
  IssueSheetContent,
  RatingChangeContent,
  GlobalSearchEntity,
} from "data/@types/GlobalSearchData";
import { Box, Typography } from "@mui/material";
import AppIcon from "ui/components/icons/AppIcon";
import {
  iconChatBubble,
  iconWorkspacePremium,
  iconDescription,
} from "ui/components/icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import useHighlight, { globalSearchMarkStyle } from "data/hooks/useHighlight";
import { useGlobalSearchContext } from "../SearchProvider";
import { extractHighlightCentralized } from "utils/highlightTextExtractor";
import TagList from "ui/components/data-display/TagList";
import GlobalSearchItemHeader from "./GlobalSearchItemHeader";
import { ActivityType } from "data/@types/ActivityCardData";
import ActivityInfo from "ui/partials/activity/ActivityInfo";
import { Link } from "react-router-dom";
import { FirmContent } from "data/@types/Firm";
import { EntityType } from "data/@types/Entity";

type Source = CommentContent | IssueSheetContent | RatingChangeContent;
type ActivityItemType = Exclude<ActivityType, "MEETING">;
type GlobalSearchActivityItemProps = {
  source: Source;
  activityType: ActivityItemType;
};

const NULL_FIRM: FirmContent = {
  id: "",
  longName: "Null Firm",
};

const getDetailsRoute = (
  entityType: EntityType,
  entity: GlobalSearchEntity | null,
  activityType: ActivityItemType,
  itemId: number,
) => {
  const activityTypeToRoute = {
    COMMENT: `/comment/${itemId}`,
    ISSUE_SHEET: `/${entityType.toLowerCase()}-details/${entity?.id}?tab=issue-sheet`,
    RATING_CHANGE: `/${entityType.toLowerCase()}-details/${entity?.id}?tab=rating-changes`,
  };

  if (activityType === "COMMENT") {
    return activityTypeToRoute[activityType];
  }

  if (!entity?.id) return "/home";

  return activityTypeToRoute[activityType] || "/home";
};

const GlobalSearchActivityItem = ({
  source,
  activityType,
}: GlobalSearchActivityItemProps) => {
  const { search, handleClose } = useGlobalSearchContext();
  const { highlight, searchRegExp } = useHighlight(
    search,
    globalSearchMarkStyle,
  );

  const sectionsAsPlainText = Object.values(source.sections || {})
    .filter(Boolean)
    .map((s) => s.replace(/(\.?\s*)$/g, ""))
    .join(". ")
    .concat(".");
  const commentPreview = extractHighlightCentralized(
    sectionsAsPlainText,
    searchRegExp,
  );

  const isProductOrStrategy = ["Product", "Strategy"].includes(
    source.entityType,
  );

  const shouldShowTextPreview =
    source.sections &&
    Object.values(source.sections).filter(Boolean).length > 0;

  const shouldShowTags =
    "tags" in source && source.tags && source.tags.length > 0;

  const activityIcons: Record<ActivityItemType, IconDefinition> = {
    RATING_CHANGE: iconWorkspacePremium,
    ISSUE_SHEET: iconDescription,
    COMMENT: iconChatBubble,
  };

  const activityIcon = activityIcons[activityType];

  const baseRoute = getDetailsRoute(
    source.entityType,
    source.entity,
    activityType,
    source.id,
  );
  const detailsRoute =
    activityType === "COMMENT" && (source as CommentContent).isAnnualMeetingNote
      ? `/annual-meeting-note/${source.id}`
      : baseRoute;

  return (
    <Box
      sx={{
        padding: "6px 0",
        display: "inline-grid",
        gridTemplateColumns: "25px auto",
        color: "inherit",
        cursor: "pointer",
        textDecoration: "none",
        width: "100%",
      }}
    >
      <AppIcon
        icon={activityIcon}
        sx={{ fontSize: "1em", gridRow: "span 5", mt: "3px" }}
        data-test-id="activity-type-icon"
      />
      <GlobalSearchItemHeader
        firm={source.firm || NULL_FIRM}
        entityType={source.entityType}
        entity={source.entity}
      />
      <Link
        to={detailsRoute}
        style={{ textDecoration: "none" }}
        onClick={handleClose}
      >
        <ActivityInfo
          entityType={source.entityType}
          author={source.authorName}
          date={source.modTimest}
          lastRating={(source as CommentContent | IssueSheetContent).lastRating}
          priorRating={(source as RatingChangeContent).priorRating}
          rating={(source as RatingChangeContent).rating}
        />
        {isProductOrStrategy && (
          <Box
            component="span"
            sx={{
              display: "block",
              fontSize: "11px",
              color: "text.secondary",
            }}
          >
            Asset Class:&nbsp;
            <span
              dangerouslySetInnerHTML={{
                __html: source.assetClass ? highlight(source.assetClass) : "--",
              }}
            />
          </Box>
        )}
        {shouldShowTextPreview && (
          <Typography
            variant="body2"
            component="span"
            fontSize="12px"
            fontStyle="italic"
            lineHeight="18px"
            display="block"
            color="text.secondary"
            dangerouslySetInnerHTML={{
              __html: highlight(commentPreview),
            }}
          />
        )}
        {shouldShowTags && (
          <TagList list={source.tags} key={source.id} style={{ pb: 0 }} />
        )}
      </Link>
    </Box>
  );
};

export default GlobalSearchActivityItem;
